/** Chinese labels + the fixed vocabularies the admin forms offer. */
import type { BatchState, Domain, MaterialKind, RecordingState, Role, Task } from "@xinghai/shared";
import type { IconId } from "@xinghai/ui";
import type { Tone } from "@xinghai/ui";

export const TASK_LABEL: Record<Task, string> = {
  T1_reference: "T1 范音",
  T1b_reference_singing: "T1b 演唱范音",
  T2_diagnostic: "T2 单字",
  T3_tongue_twister: "T3 绕口令",
  T4_passage: "T4 段落",
  T5_lyric_reading: "T5 歌词朗读",
  T6_singing: "T6 演唱",
  T7_perception: "T7 听辨",
};

export const MATERIAL_KIND_LABEL: Record<MaterialKind, string> = {
  diagnostic: "诊断字表",
  tongue_twister: "绕口令",
  paragraph: "段落",
  lyrics: "歌词",
  song: "歌曲",
  reference: "范音",
  perception: "听辨",
};

export const DOMAIN_LABEL: Record<Domain, string> = {
  reading: "朗读",
  singing: "歌唱",
};

export const BATCH_STATE_LABEL: Record<BatchState, string> = {
  draft: "草稿",
  published: "已发布",
  collecting: "采集中",
  annotating: "标注中",
  frozen: "已冻结",
};

export const BATCH_STATE_TONE: Record<BatchState, Tone> = {
  draft: "neutral",
  published: "info",
  collecting: "info",
  annotating: "warning",
  frozen: "success",
};

export const BATCH_STATE_ICON: Record<BatchState, IconId> = {
  draft: "ui-edit",
  published: "ui-upload",
  collecting: "ui-microphone",
  annotating: "ui-writing",
  frozen: "ui-lock",
};

export const RECORDING_STATE_LABEL: Record<RecordingState, string> = {
  uploaded: "待质检",
  qc_ok: "质检通过",
  qc_reject: "质检驳回",
  annotating: "标注中",
  merged: "已汇总",
  arbitrating: "待仲裁",
  final: "已入库",
};

export const RECORDING_STATE_ICON: Record<RecordingState, IconId> = {
  uploaded: "ui-inbox",
  qc_ok: "ui-check",
  qc_reject: "ui-ban",
  annotating: "ui-writing",
  merged: "ui-check-circle",
  arbitrating: "ui-swap",
  final: "ui-lock",
};

/** Display order of the 录音计数 stat row (docs/UX.md 11). */
export const RECORDING_STATE_ORDER: RecordingState[] = [
  "uploaded",
  "qc_ok",
  "annotating",
  "merged",
  "arbitrating",
  "final",
  "qc_reject",
];

export const ROLE_LABEL: Record<Role, string> = {
  student: "学生",
  annotator: "标注专家",
  adjudicator: "仲裁专家",
  teacher: "教师",
  course_team: "课程团队",
  admin: "管理员",
};

export const ROLE_ORDER: Role[] = [
  "student",
  "annotator",
  "adjudicator",
  "teacher",
  "course_team",
  "admin",
];

/** ACCOUNTS.md §5 标注者画像 — 派发式标注按 E1+E2 配对 (docs/ROADMAP.md B4). */
export const ANNOTATOR_TRACKS: Array<{
  value: "E1_phonetics" | "E2_vocal" | "E3_extra";
  label: string;
  hint: string;
}> = [
  { value: "E1_phonetics", label: "E1 普通话语音学", hint: "声韵调、IPA、音系分析" },
  { value: "E2_vocal", label: "E2 声乐 / 歌唱语言", hint: "字头字腹字尾、归韵辙位" },
  { value: "E3_extra", label: "E3 机动", hint: "补位与校准，不参与固定配对" },
];

export const ANNOTATOR_TRACK_LABEL: Record<string, string> = Object.fromEntries(
  ANNOTATOR_TRACKS.map((t) => [t.value, t.label]),
);

/** The two tracks an invitation can preset (E3 机动 is assigned later, not invited). */
export const INVITE_TRACKS = ANNOTATOR_TRACKS.filter(
  (t) => t.value === "E1_phonetics" || t.value === "E2_vocal",
);

/**
 * What an invitation can grant (`INVITABLE_ROLES`; never `student` — students
 * self-register with a class code). Multi-select, so a 教师 can also be a
 * 标注专家; a 教师 must pick a class, which is what drives 师生回避.
 */
export const INVITE_TARGETS: Array<{
  key: "annotator" | "adjudicator" | "course_team" | "teacher" | "admin";
  label: string;
  hint: string;
  roles: Role[];
  needsClass?: boolean;
  needsTrack?: boolean;
}> = [
  {
    key: "annotator",
    label: "标注专家",
    hint: "逐字标注；需要选 E1 语音学 或 E2 声乐方向",
    roles: ["annotator"],
    needsTrack: true,
  },
  { key: "adjudicator", label: "仲裁", hint: "处理两位专家的冲突项", roles: ["adjudicator"] },
  {
    key: "course_team",
    label: "课程团队",
    hint: "素材、批次、派发、质检、导出",
    roles: ["course_team"],
  },
  {
    key: "teacher",
    label: "教师",
    hint: "必须绑定班级；该班学生的录音会对其回避",
    roles: ["teacher"],
    needsClass: true,
  },
  { key: "admin", label: "管理员", hint: "账号管理；请谨慎授予", roles: ["admin"] },
];

/** ACCOUNTS.md §2 — 邀请链接有效期选项。管理员默认仍是 72 小时。 */
export const INVITE_EXPIRY_OPTIONS = [
  { value: "24", label: "1 天" },
  { value: "72", label: "72 小时（默认）" },
  { value: "168", label: "7 天" },
  { value: "720", label: "30 天" },
  { value: "8760", label: "1 年" },
];

/** `audit_log.action` → 中文 (ACCOUNTS.md §7). Unknown actions fall back to the raw code. */
export const AUDIT_ACTION_LABEL: Record<string, string> = {
  "auth.login": "登录成功",
  "auth.login_failed": "登录失败",
  "auth.logout": "登出",
  "auth.register": "自助注册",
  "auth.consent": "签署授权",
  "auth.consent.withdraw": "撤回授权",
  "admin.invitation.create": "生成邀请链接",
  "admin.invitation.use": "邀请链接被使用",
  "admin.invitation.revoke": "撤销邀请链接",
  "admin.reset_link.create": "生成重置链接",
  "admin.reset_link.use": "重置链接被使用",
  "admin.user.create": "创建账号",
  "admin.user.update": "修改账号",
  "admin.user.disable": "停用账号",
  "admin.session.revoke": "吊销会话",
  "speakers.verify": "核验说话人",
  "speakers.update": "修改说话人",
  "speakers.import": "导入说话人",
  "materials.import": "导入素材",
  "materials.freeze": "冻结素材",
  "batch.create": "创建批次",
  "batch.publish": "发布批次",
  "batch.dispatch": "派发批次",
  "recording.qc.qc_ok": "质检通过",
  "recording.qc.qc_reject": "质检驳回",
  "recording.golden": "设为黄金题",
  "export.freeze": "冻结导出版本",
  "class.create": "创建班级",
  "class.invite.rotate": "轮换班级邀请码",
};

export function auditActionLabel(action: string): string {
  return AUDIT_ACTION_LABEL[action] ?? action;
}

/** §2.3 the eight dialect groups the dispatch prior knows (services/dispatch.ts). */
export const DIALECT_GROUPS = [
  "粤语",
  "客家",
  "潮汕",
  "西南官话",
  "吴语",
  "闽语",
  "湘赣",
  "北方官话",
] as const;

/** Seed vocabulary for 训练水平 (`apps/server/seed/users.json`). */
export const TRAINING_LEVELS: Array<{ value: string; label: string }> = [
  { value: "vocal", label: "声乐方向" },
  { value: "non_vocal", label: "非声乐方向" },
  { value: "freshman", label: "新生" },
];

export const DISPATCH_STRATEGIES: Array<{
  value: "manual" | "dialect_prior" | "round_robin";
  label: string;
  hint: string;
}> = [
  {
    value: "manual",
    label: "手动指定",
    hint: "所选素材按当前顺序，原样派给每一位说话人。",
  },
  {
    value: "dialect_prior",
    label: "方言先验",
    hint: "按说话人母方言的易混淆类给素材排序，风险高的先录；不会剔除任何素材。",
  },
  {
    value: "round_robin",
    label: "轮转错峰",
    hint: "每位说话人的素材顺序依次错开一位，避免所有人同时录同一条。",
  },
];

/** §14 红线 + 结构审计码 → 中文解释 (packages/shared/src/manifest.ts). */
export const AUDIT_CODE_LABEL: Record<string, string> = {
  "R14.1": "14.1 歌唱声调泄漏 — 歌唱行带了普通话声调监督",
  "R14.3": "14.3 诱发音进训练 — induced 录音必须分流到 product_materials",
  "R14.4": "14.4 tri-state — 缺标被当成 0，应保留 null / 掩码",
  "R14.5": "14.5 未锁定拼音 — 素材有字缺 pinyin_locked",
  "R14.6": "14.6 授权未全签 — 三项授权或监护人联签缺失",
  CONSENT_WITHDRAWN: "授权已撤回 — 该说话人的行不得导出",
  LEN: "结构错误 LEN — 数组长度对不上",
  REF: "结构错误 REF — 索引越界",
  DIM: "结构错误 DIM — 维度数不对（word_mark_targets 应为 9 维）",
  RANGE: "结构错误 RANGE — 数值超出 [0,1]",
  REQ: "结构错误 REQ — 必填字段缺失",
  MATERIAL_MISSING: "素材缺失 — 找不到录音对应的素材",
  MERGE_MISSING: "未汇总 — 该录音不足两份判断，无法进 manifest",
  LYRICS_IN_COPYRIGHT: "歌词仍在著作权保护期，商用前需取得授权",
  OVER_TRAINING_WINDOW: "超出训练窗口 — 录音长于 §9 的 22 s，已改入 product_materials",
  TEACHER_STUDENT_OVERLAP: "师生重叠 — 该说话人被本班教师标注（§1.5 回避策略）",
  SPEAKER_UNVERIFIED: "说话人未核验 — 自助注册的学生尚未经课程团队核验",
};

export function auditCodeLabel(code: string): string {
  return AUDIT_CODE_LABEL[code] ?? `未知审计码 ${code}`;
}
